import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { baseUrlApi } from '../../utils/url';

interface Lease {
  id: number;
  start: string;
  end: string | null;
  active: boolean;
  caution: number;
  typeb?: string;
  activite?: string;
  TenantId: number;
  rents?: any[];
  tenant?: any;
}

interface LeaseState {
  leases: Lease[];
  lease: Lease | null;
  leaseRents: Record<string, any>[];  // ✅ Alternative temporaire
  activeLeaseId: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: LeaseState = {
  leases: [],
  lease: null,
  leaseRents: [],
  activeLeaseId: null,
  loading: false,
  error: null,
};

export const fetchLeases = createAsyncThunk(
  'lease/fetchAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const ownerId = state.authentification.user.OwnerId;
      const token = state.authentification.user.token;
      
      const response = await axios.get(`${baseUrlApi}/leases/all/${ownerId}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leases');
    }
  }
);

export const fetchLease = createAsyncThunk(
  'lease/fetchOne',
  async (leaseId: number, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      
      const response = await axios.get(`${baseUrlApi}/leases/${leaseId}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      dispatch(fetchLeases());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch lease');
    }
  }
);

export const fetchLeaseRents = createAsyncThunk(
  'lease/fetchRents',
  async ({ leaseId, codate }: { leaseId: number; codate: string }, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      const response = await axios.get(`${baseUrlApi}/leases/Loylocat/${leaseId}/${codate}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch lease rents');
    }
  }
);

export const createLease = createAsyncThunk(
  'lease/create',
  async (leaseData: any, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      
      const response = await axios.post(`${baseUrlApi}/leases`, leaseData, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      dispatch(fetchLeases());
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "L'ajout du Bail a échoué !");
    }
  }
);

export const createLeaseRent = createAsyncThunk(
  'lease/createLeaseRent',
  async ({ leaseData, rentData }: { leaseData: any; rentData: any }, { getState, dispatch, rejectWithValue }) => {
    try {
      if (!leaseData || !leaseData.TenantId) {
        throw new Error("TenantId est requis dans leaseData");
      }
      if (!rentData) {
        throw new Error("Les données de loyer sont manquantes");
      }

      const state: any = getState();
      const token = state.authentification.user.token;
      
      const response = await axios.post(`${baseUrlApi}/leases/leasesRent`, {
        leaseData,
        rentData,
      }, {
        headers: { Authorization: `bearer ${token}` }
      });

      const leaseId = response.data.leaseId || response.data.lease?.id;
      if (!leaseId) {
        throw new Error("LeaseId not found in the response");
      }

      dispatch(fetchLeases());

      return { ...response.data, leaseId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || "L'ajout du Bail a échoué !");
    }
  }
);

export const updateLease = createAsyncThunk(
  'lease/update',
  async ({ leaseId, leaseData }: { leaseId: number; leaseData: any }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      
      const response = await axios.put(`${baseUrlApi}/leases/${leaseId}`, leaseData, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      dispatch(fetchLeases());
      dispatch(fetchLease(leaseId));
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update lease');
    }
  }
);

export const closeLeaseEnd = createAsyncThunk(
  'lease/closeEnd',
  async ({ leaseId, userData }: { leaseId: number; userData: any }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      const response = await axios.put(`${baseUrlApi}/leases/${leaseId}`, userData, {
        headers: { Authorization: `bearer ${token}` }
      });
      dispatch(fetchLease(leaseId));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to close lease');
    }
  }
);

const leaseSlice = createSlice({
  name: 'lease',
  initialState,
  reducers: {
    encoursLease: (state, action: PayloadAction<number | null>) => {
      if (action.payload === null || action.payload === 0) {
        state.activeLeaseId = null;
        state.lease = null;
        return;
      }
      
      state.activeLeaseId = action.payload;
      
      const lease = state.leases.find(l => l.id === action.payload);
      if (lease) {
        state.lease = lease;
      }
    },
    
    clearActiveLease: (state) => {
      state.activeLeaseId = null;
      state.lease = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeases.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLeases.fulfilled, (state, action) => {
        state.loading = false;
        state.leases = action.payload;
      })
      .addCase(fetchLeases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(fetchLease.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLease.fulfilled, (state, action) => {
        state.loading = false;
        state.lease = action.payload;
      })
      .addCase(fetchLease.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(fetchLeaseRents.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLeaseRents.fulfilled, (state, action) => {
        state.loading = false;
        state.leaseRents = action.payload;
      })
      .addCase(fetchLeaseRents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(createLease.pending, (state) => {
        state.loading = true;
      })
      .addCase(createLease.fulfilled, (state, action) => {
        state.loading = false;
        state.activeLeaseId = action.payload.id;
        state.lease = action.payload;
        // Ne pas pousser dans state.leases ici : le bail n'a pas le tenant associé.
        // fetchLeases() est déjà dispatché dans createLease et mettra à jour state.leases.
      })
      .addCase(createLease.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(createLeaseRent.pending, (state) => {
        state.loading = true;
      })
      .addCase(createLeaseRent.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.leaseId) {
          state.activeLeaseId = action.payload.leaseId;
        }
        // Pas de push : même raison que createLease — fetchLeases() est dispatché dans le thunk.
      })
      .addCase(createLeaseRent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateLease.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateLease.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.leases.findIndex(l => l.id === action.payload.id);
        if (index !== -1) {
          state.leases[index] = action.payload;
        }
      })
      .addCase(updateLease.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(closeLeaseEnd.pending, (state) => {
        state.loading = true;
      })
      .addCase(closeLeaseEnd.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(closeLeaseEnd.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { encoursLease, clearActiveLease } = leaseSlice.actions;
export default leaseSlice.reducer;